import React, { useRef, useState } from "react";
import { Rect } from "react-konva";
import { Portal } from "react-konva-utils";
import { IRect } from "frontend/utils/math-utils";
import KonvaTooltip from "frontend/canvas-designer-new/utility-elements/konva-tooltip";
import { doesTextOverflow } from "./text-measurement";
import { OrgChartNodeTextStyles } from "./utils";

type OverflowTooltipOverlayProps = {
  rect: IRect;
  displayText: string;
  textStyleId: OrgChartNodeTextStyles;
  fieldTitle?: string;
  placement?: "top" | "right";
  bgColor?: string;
  textColor?: string;
  borderRadius?: number;
  offset?: number;
  suppress?: boolean;
  onDblClick?: () => void;
  isHovering: boolean;
  setIsHovering: (isHovering: boolean) => void;
};

export function OverflowTooltipOverlay({
  rect,
  displayText,
  textStyleId,
  fieldTitle,
  placement = "right",
  bgColor = "#DEE1EB",
  textColor = "#1E1E1E",
  offset = 16,
  suppress = false,
  onDblClick,
  isHovering,
  setIsHovering,
}: OverflowTooltipOverlayProps) {
  const [anchorAbs, setAnchorAbs] = useState<{ x: number; y: number } | null>(null);
  const tooltipShowTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const anchorRectRef = useRef<any>(null);
  const rafRef = useRef<number | null>(null);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const isOverflowing = doesTextOverflow(displayText, rect.width, textStyleId, fieldTitle);

  if (!isOverflowing || suppress) return null;

  return (
    <>
      <Rect
        ref={anchorRectRef}
        x={rect.x}
        y={rect.y}
        width={rect.width}
        height={rect.height}
        fill="transparent"
        listening={true}
        onMouseEnter={(e) => {
          tooltipShowTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
          }, 1000);
          const node = e.currentTarget;
          const stage = node.getStage();
          if (!stage) return;

          const abs = node.getAbsolutePosition();
          const transform = stage.getAbsoluteTransform().copy();
          transform.invert();
          const stageCoords = transform.point(abs);
          setAnchorAbs({ x: Math.round(stageCoords.x), y: Math.round(stageCoords.y) });

          startPosRef.current = { x: abs.x, y: abs.y };
          const tick = () => {
            if (!anchorRectRef.current) return;
            const parent = anchorRectRef.current.getParent()?.getParent?.();
            const dragging = typeof parent?.isDragging === "function" ? parent.isDragging() : false;
            const pos = anchorRectRef.current.getAbsolutePosition();
            const start = startPosRef.current;
            const moved = !!start && (Math.abs(pos.x - start.x) > 0.5 || Math.abs(pos.y - start.y) > 0.5);
            if (dragging || moved) {
              setIsHovering(false);
              setAnchorAbs(null);
              rafRef.current && cancelAnimationFrame(rafRef.current);
              rafRef.current = null;
              return;
            }
            rafRef.current = requestAnimationFrame(tick);
          };
          rafRef.current = requestAnimationFrame(tick);
        }}
        onMouseLeave={() => {
          if (tooltipShowTimeoutRef.current) {
            clearTimeout(tooltipShowTimeoutRef.current);
          }
          setAnchorAbs(null);
          setIsHovering(false);
          if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
          }
        }}
        onDblClick={onDblClick}
      />
      <Portal enabled selector=".Overlay">
        {isHovering && anchorAbs && (
          <KonvaTooltip
            x={anchorAbs.x}
            y={anchorAbs.y}
            parentWidth={rect.width}
            text={displayText}
            placement={placement}
            anchorHeight={rect.height}
            offset={offset}
            bgColor={bgColor}
            textColor={textColor}
            animationDuration={0.2}
          />
        )}
      </Portal>
    </>
  );
}
